import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/password-input/PasswordInput';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await signUp(email, password, displayName);
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Check your email for a confirmation link!');
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 relative overflow-hidden bg-background">
      {/* Left side: Form */}
      <div className="flex flex-col justify-center items-center p-8 lg:p-12 relative z-10">
        <div className="w-full max-w-sm space-y-8">
          
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/logo.svg" alt="Koin Logo" className="h-16 w-auto" />
          </div>
          
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">Create your account</h1>
            <p className="text-muted-foreground">Start tracking your finances with Koin</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Display Name</Label>
              <Input 
                id="name" 
                placeholder="John Doe" 
                value={displayName} 
                onChange={(e) => setDisplayName(e.target.value)} 
                required 
                className="h-11"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                placeholder="you@example.com" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                required 
                className="h-11"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput 
                id="password" 
                placeholder="••••••••" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
                minLength={6} 
                className="h-11"
              />
            </div>
            
            <Button type="submit" className="w-full h-11 text-base font-medium transition-all" disabled={loading}>
              {loading ? 'Creating account…' : 'Sign Up'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right side: Decorative */}
      <div className="hidden lg:flex flex-col justify-center items-center bg-primary/5 relative overflow-hidden border-l border-border/40">
         {/* Blur effects & gradients */}
         <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background" />
         <div className="absolute -top-1/4 -right-1/4 w-[800px] h-[800px] bg-primary/20 rounded-full blur-[100px] opacity-70 pointer-events-none" />
         <div className="absolute -bottom-1/4 -left-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[80px] opacity-70 pointer-events-none" />
         
         {/* Logo Showcase */}
         <div className="relative z-10 flex flex-col items-center max-w-lg text-center px-12">
            <div className="p-4 bg-background/50 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 mb-10">
              <img src="/logo.svg" alt="Koin Logo" className="h-32 w-auto drop-shadow-xl" />
            </div>
            <h2 className="text-4xl font-bold tracking-tight mb-4 text-foreground/90">
              Your Financial Hub
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Join Koin today and get an unparalleled overview of your wealth, investments, and expenses all in one beautifully designed platform.
            </p>
         </div>
      </div>
    </div>
  );
}
